import { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import api from '../../lib/api';
import { 
  Users, Plus, Edit, Trash2, UserPlus, UserMinus, Search, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';

const AdminGroups = () => {
  const [groups, setGroups] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Create/Edit modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupName, setGroupName] = useState('');
  const [groupDescription, setGroupDescription] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Members modal
  const [membersModalOpen, setMembersModalOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupMembers, setGroupMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  
  // Delete modal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);

  useEffect(() => {
    fetchGroups();
    fetchAllUsers();
  }, []);

  const fetchGroups = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/groups');
      setGroups(response.data.groups || []);
    } catch (error) {
      toast.error('Failed to load groups');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await api.get('/admin/users?limit=500');
      setAllUsers(response.data.users || []);
    } catch (error) {
      console.error('Failed to fetch users');
    }
  };

  const fetchGroupMembers = async (groupId) => {
    setLoadingMembers(true);
    try {
      const response = await api.get(`/admin/groups/${groupId}`);
      setGroupMembers(response.data.members || []);
    } catch (error) {
      toast.error('Failed to load members');
      setGroupMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  };

  // Create/Edit Group
  const openCreateModal = () => {
    setEditingGroup(null);
    setGroupName('');
    setGroupDescription('');
    setEditModalOpen(true);
  };

  const openEditModal = (group) => {
    setEditingGroup(group);
    setGroupName(group.name);
    setGroupDescription(group.description || '');
    setEditModalOpen(true);
  };

  const handleSaveGroup = async () => {
    if (!groupName.trim()) {
      toast.error('Group name is required');
      return;
    }
    
    setSaving(true);
    try {
      if (editingGroup) {
        await api.patch(`/admin/groups/${editingGroup.group_id}`, {
          name: groupName.trim(),
          description: groupDescription.trim()
        });
        toast.success('Group updated');
      } else {
        await api.post('/admin/groups', {
          name: groupName.trim(),
          description: groupDescription.trim()
        });
        toast.success('Group created');
      }
      setEditModalOpen(false);
      fetchGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save group');
    } finally {
      setSaving(false);
    }
  };

  // Members Management
  const openMembersModal = async (group) => {
    setSelectedGroup(group);
    setMemberSearch('');
    setSelectedUsers([]);
    setMembersModalOpen(true);
    await fetchGroupMembers(group.group_id);
  };

  const handleAddMembers = async () => {
    if (!selectedGroup || selectedUsers.length === 0) return;
    
    try {
      await api.post(`/admin/groups/${selectedGroup.group_id}/members`, {
        user_ids: selectedUsers
      });
      toast.success('Members added');
      setSelectedUsers([]);
      fetchGroupMembers(selectedGroup.group_id);
      fetchGroups();
    } catch (error) {
      toast.error('Failed to add members');
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!selectedGroup) return;
    
    try {
      await api.delete(`/admin/groups/${selectedGroup.group_id}/members`, {
        data: { user_ids: [userId] }
      });
      toast.success('Member removed');
      fetchGroupMembers(selectedGroup.group_id);
      fetchGroups();
    } catch (error) {
      toast.error('Failed to remove member');
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // Delete Group
  const openDeleteModal = (group) => {
    setGroupToDelete(group);
    setDeleteModalOpen(true);
  };

  const handleDeleteGroup = async () => {
    if (!groupToDelete) return;
    
    try {
      await api.delete(`/admin/groups/${groupToDelete.group_id}`);
      toast.success('Group deleted');
      setDeleteModalOpen(false);
      fetchGroups();
    } catch (error) {
      toast.error('Failed to delete group');
    }
  };

  // Filter users not in current group for add members
  const availableUsers = allUsers.filter(u => 
    !groupMembers.some(m => m.user_id === u.user_id) &&
    (memberSearch === '' || 
     u.name.toLowerCase().includes(memberSearch.toLowerCase()) ||
     u.email.toLowerCase().includes(memberSearch.toLowerCase()))
  );

  return (
    <AdminLayout>
      <div className="p-6 md:p-8 max-w-6xl mx-auto" data-testid="admin-groups-page">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Manrope' }}>
              User Groups
            </h1>
            <p className="text-gray-600 dark:text-gray-400">Manage user groups for access control</p>
          </div>
          <Button 
            onClick={openCreateModal}
            className="bg-[#4A9CC7] hover:bg-[#3d8ab5]"
            data-testid="create-group-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Group
          </Button>
        </div>

        {/* Groups Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#4A9CC7]" />
          </div>
        ) : groups.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <Users className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium mb-2">No groups yet</h3>
              <p className="text-gray-500 mb-4">Create your first user group to organize users</p>
              <Button onClick={openCreateModal} variant="outline">
                <Plus className="w-4 h-4 mr-2" />
                Create Group
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {groups.map(group => (
              <Card key={group.group_id} className="hover:shadow-md transition-shadow" data-testid={`group-${group.group_id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Users className="w-5 h-5 text-[#4A9CC7]" />
                        {group.name}
                      </CardTitle>
                      {group.description && (
                        <CardDescription className="mt-1">{group.description}</CardDescription>
                      )}
                    </div>
                    <Badge variant="secondary">{group.member_count || 0} members</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between pt-2 border-t">
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      Created by {group.created_by_name}
                    </span>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openMembersModal(group)} title="Manage Members">
                        <UserPlus className="w-4 h-4 text-[#4A9CC7]" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => openEditModal(group)} title="Edit Group">
                        <Edit className="w-4 h-4 text-gray-500" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => openDeleteModal(group)} title="Delete Group">
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create/Edit Group Modal */}
        <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {editingGroup ? <Edit className="w-5 h-5 text-[#4A9CC7]" /> : <Plus className="w-5 h-5 text-[#4A9CC7]" />}
                {editingGroup ? 'Edit Group' : 'Create Group'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Group Name *</Label>
                <Input
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  placeholder="e.g., All Finance Users"
                  className="mt-1"
                  data-testid="group-name-input"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={groupDescription}
                  onChange={(e) => setGroupDescription(e.target.value)}
                  placeholder="Describe the purpose of this group"
                  className="mt-1"
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditModalOpen(false)}>Cancel</Button>
              <Button onClick={handleSaveGroup} disabled={saving} className="bg-[#4A9CC7] hover:bg-[#3d8ab5]">
                {saving ? 'Saving...' : editingGroup ? 'Save Changes' : 'Create Group'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Members Management Modal */}
        <Dialog open={membersModalOpen} onOpenChange={setMembersModalOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-[#4A9CC7]" />
                Manage Members: {selectedGroup?.name}
              </DialogTitle>
              <DialogDescription>
                Add or remove users from this group
              </DialogDescription>
            </DialogHeader>
            
            <div className="flex-1 overflow-y-auto py-4 space-y-6">
              {/* Current Members */}
              <div>
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  Current Members
                  <Badge variant="secondary">{groupMembers.length}</Badge>
                </h4>
                {loadingMembers ? (
                  <div className="text-center py-4 text-gray-500">Loading...</div>
                ) : groupMembers.length === 0 ? (
                  <p className="text-gray-500 text-sm">No members yet</p>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {groupMembers.map(member => (
                      <div 
                        key={member.user_id} 
                        className="flex items-center justify-between p-3 bg-gray-50 dark:bg-slate-800 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-[#4A9CC7]/10 flex items-center justify-center">
                            <span className="text-[#4A9CC7] text-sm font-medium">{member.name?.charAt(0)}</span>
                          </div>
                          <div>
                            <p className="font-medium text-sm">{member.name}</p>
                            <p className="text-xs text-gray-500">{member.email}</p>
                          </div>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => handleRemoveMember(member.user_id)}
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        >
                          <UserMinus className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Add Members */}
              <div>
                <h4 className="font-medium mb-3">Add Members</h4>
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search users..."
                    value={memberSearch}
                    onChange={(e) => setMemberSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>
                
                {availableUsers.length === 0 ? (
                  <p className="text-gray-500 text-sm">No users available to add</p>
                ) : (
                  <>
                    <div className="space-y-2 max-h-48 overflow-y-auto mb-3">
                      {availableUsers.slice(0, 20).map(user => (
                        <div 
                          key={user.user_id}
                          className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                            selectedUsers.includes(user.user_id) 
                              ? 'bg-[#4A9CC7]/10 border border-[#4A9CC7]' 
                              : 'bg-gray-50 dark:bg-slate-800 hover:bg-gray-100'
                          }`}
                          onClick={() => toggleUserSelection(user.user_id)}
                        >
                          <Checkbox 
                            checked={selectedUsers.includes(user.user_id)}
                            onChange={() => {}}
                          />
                          <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                            <span className="text-gray-600 dark:text-gray-300 text-sm font-medium">{user.name?.charAt(0)}</span>
                          </div>
                          <div>
                            <p className="font-medium text-sm">{user.name}</p>
                            <p className="text-xs text-gray-500">{user.email}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {selectedUsers.length > 0 && (
                      <Button 
                        onClick={handleAddMembers}
                        className="w-full bg-[#4A9CC7] hover:bg-[#3d8ab5]"
                      >
                        <UserPlus className="w-4 h-4 mr-2" />
                        Add {selectedUsers.length} Member{selectedUsers.length > 1 ? 's' : ''}
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setMembersModalOpen(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Modal */}
        <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <Trash2 className="w-5 h-5" />
                Delete Group
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to delete "{groupToDelete?.name}"? 
                This will remove all {groupToDelete?.member_count || 0} members from the group.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>Cancel</Button>
              <Button variant="destructive" onClick={handleDeleteGroup}>Delete Group</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminGroups;
